import { Button } from "@fluentui/react-components";
import React, { Component, ErrorInfo } from "react";
import { createPortal } from "react-dom";
import { formatErrorForDisplay } from "../../utils/errorHandling";
import { ApiError } from "../../types/ApiError";
import "./ErrorBoundary.css";

interface ErrorBoundaryProps {
  resetCallback?: () => void; 
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage: string;
  errorTitle?: string;
  errorStatus?: number;
  errorDetail?: string;
}

class ErrorBoundary extends Component<React.PropsWithChildren<ErrorBoundaryProps>, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    const errorMessage = formatErrorForDisplay(error);
    
    if (error instanceof ApiError) {
      return {
        hasError: true,
        errorMessage,
        errorTitle: error.title,
        errorStatus: error.status,
        errorDetail: error.detail,
      };
    }
    
    return { 
      hasError: true, 
      errorMessage,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
  }

  closeModal = () => {
    this.setState({ 
      hasError: false, 
      errorMessage: "",
      errorTitle: undefined,
      errorStatus: undefined,
      errorDetail: undefined,
    });
    if (this.props.resetCallback) {
      this.props.resetCallback(); 
    }
  };

  render() {
    return (
      <>
        {this.props.children}
        {this.state.hasError && (
          <div className="error-boundary-overlay">
            <div className="error-boundary-modal">
              <h4 className="error-boundary-title">
                {this.state.errorTitle || 'Oops!'}
                {this.state.errorStatus && ` (${this.state.errorStatus})`}
              </h4>
              <p className="error-boundary-message">
                {this.state.errorMessage}
              </p>
              {this.state.errorDetail && this.state.errorDetail !== this.state.errorMessage && (
                <p className="error-boundary-message error-boundary-detail">
                  {this.state.errorDetail}
                </p>
              )}
              <Button 
                appearance="primary"
                className="error-boundary-close-button"
                onClick={this.closeModal}
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </>
    );
  }
}

export default ErrorBoundary;
